import { ROOM_TYPE_IDS } from 'features/Home/constants/room.constants'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'

import { mergeReorderedIds, orderSavedIds } from './scheduleInstance'

export type ScheduleRoomOption = {
  id: string
  text: string
  locked: boolean
}

export type ScheduleRoomsPreference = {
  order: string[]
  hidden: string[]
}

export const isLockedScheduleRoom = (
  room: Pick<IPatientRoom, 'is_waiting_room' | 'room_type_id'>,
): boolean => Boolean(room.is_waiting_room) || room.room_type_id === ROOM_TYPE_IDS.WAITING_ROOM

export const compareScheduleRooms = (left: IPatientRoom, right: IPatientRoom): number => {
  const leftLocked = isLockedScheduleRoom(left)
  const rightLocked = isLockedScheduleRoom(right)
  if (leftLocked && !rightLocked) return -1
  if (!leftLocked && rightLocked) return 1
  return (left.order ?? 0) - (right.order ?? 0)
}

export const toScheduleRoomOptions = (rooms: IPatientRoom[]): ScheduleRoomOption[] =>
  [...rooms].sort(compareScheduleRooms).map((room) => ({
    id: String(room.value),
    text: room.short_name || room.label,
    locked: isLockedScheduleRoom(room),
  }))

export const visibleScheduleRoomIds = (
  options: ScheduleRoomOption[],
  order: string[],
  hidden: string[],
): string[] => {
  const byId = new Map(options.map((room) => [room.id, room]))
  const hiddenSet = new Set(hidden)
  return order.filter((id) => {
    const room = byId.get(id)
    if (!room) return false
    return room.locked || !hiddenSet.has(id)
  })
}

export const orderScheduleRooms = (
  rooms: IPatientRoom[],
  selectedIds: string[] | null | undefined,
): IPatientRoom[] => {
  if (selectedIds == null) return [...rooms].sort(compareScheduleRooms)

  const byValue = new Map(rooms.map((room) => [room.value, room]))
  const ordered = selectedIds.flatMap((id) => {
    const room = byValue.get(id)
    return room ? [room] : []
  })
  const included = new Set(ordered.map((room) => room.value))
  const missingLocked = [...rooms]
    .sort(compareScheduleRooms)
    .filter((room) => isLockedScheduleRoom(room) && !included.has(room.value))
  return [...ordered, ...missingLocked]
}

// Every room in the filter's order; without one the list stays as given, unknown rooms keep their place after it.
export const sortScheduleRooms = (
  rooms: IPatientRoom[],
  order: string[] | null | undefined,
): IPatientRoom[] => {
  if (!order?.length) return rooms
  const rank = new Map(order.map((id, index) => [id, index]))
  const rankOf = (room: IPatientRoom) => rank.get(String(room.value)) ?? order.length
  return rooms
    .map((room, index) => ({ room, index }))
    .sort((left, right) => rankOf(left.room) - rankOf(right.room) || left.index - right.index)
    .map(({ room }) => room)
}

export const hiddenRoomIdsFromSessionSelection = (
  options: ScheduleRoomOption[],
  selectedIds: string[] | null,
): string[] => {
  if (!selectedIds?.length || !options.length) return []
  const selected = new Set(selectedIds.map(String))
  return options.filter((room) => !room.locked && !selected.has(room.id)).map((room) => room.id)
}

export const applyRoomsPreferenceChange = (
  previous: ScheduleRoomsPreference,
  change: ScheduleRoomsPreference,
  todayIds: string[],
  lockedIds: string[],
): ScheduleRoomsPreference => {
  const today = new Set(todayIds)
  const locked = new Set(lockedIds)
  const order = mergeReorderedIds(previous.order, change.order)
  const hidden = [
    ...previous.hidden.filter((id) => !today.has(id) && !locked.has(id)),
    ...change.hidden.filter((id) => today.has(id) && !locked.has(id)),
  ]
  return {
    order: order.filter((id, index) => order.indexOf(id) === index),
    hidden: hidden.filter((id, index) => !locked.has(id) && hidden.indexOf(id) === index),
  }
}

export const resolveScheduleRoomOrder = (defaultIds: string[], savedOrder: string[]): string[] =>
  orderSavedIds(defaultIds, savedOrder)

export const shiftRoomOrder = (order: string[], id: string, direction: -1 | 1): string[] => {
  const next = order.slice()
  const index = next.indexOf(id)
  const target = index + direction
  if (index < 0 || target < 0 || target >= next.length) return order
  ;[next[index], next[target]] = [next[target], next[index]]
  return next
}
